import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { Me } from "@/api/types";
import { configureApi } from "@/api/client";
import { clearSession, demoPersona, fetchMe, forceRenew, getToken, hasSession } from "@/auth/session";
import { OverlayProvider } from "@/ui/overlay";
import { ConsoleProvider } from "./ConsoleContext";
import { Shell } from "./Shell";
import { Login } from "@/auth/Login";
import { useTheme } from "./theme";

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (n, err) => n < 2 && !(err && typeof err === "object" && "status" in err && Number(err.status) < 500),
        refetchOnWindowFocus: false,
        // Sem placeholder global: cada lista usa `keepWithinScope` (queryKey.ts), que nunca
        // reaproveita dado de outro escopo.
      },
    },
  });
}

export function App({ client }: { client?: QueryClient }) {
  const [qc] = useState(() => client ?? makeQueryClient());
  const [me, setMe] = useState<Me | null | undefined>(hasSession() ? undefined : null);
  useTheme();

  useEffect(() => {
    configureApi({
      getToken,
      renew: forceRenew,
      headers: () => ({ "X-Demo-Persona": demoPersona() }),
      onUnauthorized: () => {
        clearSession();
        qc.clear();
        setMe(null);
      },
    });
    if (me === undefined) void fetchMe().then(setMe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <QueryClientProvider client={qc}>
      {me === undefined ? null : me === null ? (
        <OverlayProvider>
          <Login onLogin={setMe} />
        </OverlayProvider>
      ) : (
        <ConsoleProvider key={me.id} me={me} onMeChange={setMe}>
          {/* Dentro do ConsoleProvider: painéis enxergam escopo e pessoa. */}
          <OverlayProvider>
            <Shell />
          </OverlayProvider>
        </ConsoleProvider>
      )}
    </QueryClientProvider>
  );
}
